import _ from 'lodash';
import { computeDestinationPoint } from 'geolib';
import * as TaskUtilities from '../../HelperMethods/TaskUtilities';
import { STOP_TYPE, SHAPE_TYPE } from './constants';

const GEOFENCE_TYPES = {
  RADIAL: 'radial',
  POLYGON: 'polygon',
};

export function getLocationsForCustomFit(showActualVsPlannedView, markers, routeData) {
  let locations = [];
  if (!showActualVsPlannedView) {
    locations = markers.map((marker) => marker.location);
    return locations;
  }
  locations = Object.keys(routeData).flatMap((routeDataKey) => {
    const stopLocations = _.get(routeData, `${routeDataKey}.stops`, []).map((stop) => stop.location);
    const liveLocations = _.get(routeData, `${routeDataKey}.locations`, []).map((loc) => loc.location);
    return [...stopLocations, ...liveLocations];
  });
  return locations;
}

export function createArrayOfLinesFromLocations(locations) {
  const a = locations.map((location, index) => {
    if (index === locations.length - 1) return null;
    return [location, locations[index + 1]];
  });
  return a.slice(0, -1);
}

export function getUniqueStopMarkers(stopMarkers) {
  const stopMarkerXidDict = {};
  stopMarkers.forEach((marker, i) => {
    stopMarkerXidDict[marker.xid || marker.stopId] = stopMarkerXidDict[marker.xid] || { ...marker, sequence: [] };
    stopMarkerXidDict[marker.xid || marker.stopId].sequence.push(i + 1);
  });
  return Object.values(stopMarkerXidDict);
}

export function getMaxDurationOfStopInMinutes(routeData) {
  const durationInPlannedStops = routeData.planned.stops.map((s) => s.durationInMinutes).filter((d) => d != null);
  const durationInActualStops = routeData.actual.stops.map((s) => s.durationInMinutes).filter((d) => d != null);
  return Math.max(...durationInActualStops, ...durationInPlannedStops);
}

export function getInfoForRouteData(routeData) {
  if (
    !routeData ||
    ((!routeData.stops || !routeData.stops.length) && (!routeData.locations || !routeData.locations.length))
  ) {
    return 'No Data Available';
  }
  if (!routeData.stops || !routeData.stops.length) {
    return 'No Locations Available';
  }
  return null;
}

export const getAssigneeNamesAndCount = (assigneeIds, assigneeObject) => {
  const assignees = [];
  let remainingCount = 0;
  if (assigneeIds) {
    assigneeIds
      .filter((assigneeId) => assigneeId)
      .forEach((assigneeId, index) => {
        if (index > 1) {
          remainingCount += 1;
        } else {
          assignees.push(assigneeObject[assigneeId]);
        }
      });
  }
  return { assignees, remainingCount };
};

export function isPointInPolygon(latitude, longitude, polygon) {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    console.error('Invalid latitude or longitude. Numbers are expected');
    return false;
  }
  if (!polygon || !Array.isArray(polygon)) {
    console.error('Invalid polygon. Array with locations expected');
    return false;
  }
  if (polygon.length === 0) {
    console.error('Invalid polygon. Non-empty Array expected');
    return false;
  }

  const x = latitude;
  const y = longitude;

  let inside = false;
  // eslint-disable-next-line no-plusplus
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
}

function isPointInsideCircle(checkPoint, centerPoint, km) {
  const ky = 40000 / 360;
  const kx = Math.cos((Math.PI * centerPoint.lat) / 180.0) * ky;
  const dx = Math.abs(centerPoint.lng - checkPoint.lng) * kx;
  const dy = Math.abs(centerPoint.lat - checkPoint.lat) * ky;
  return Math.sqrt(dx * dx + dy * dy) <= km;
}

export function handleShape(shape, markers = []) {
  const reqTasks = [];
  if (shape.type === SHAPE_TYPE.POLYGON) {
    const reqPolygonPoints = shape.getPath().getArray();
    const polygon = reqPolygonPoints.map((point) => {
      return [point.lat(), point.lng()];
    });
    for (const marker of markers) {
      const {
        location: { lat, lng },
        taskList = [],
        type,
      } = marker;
      if (type !== STOP_TYPE.USER_CURRENT_LOCATION_STOP && isPointInPolygon(lat, lng, polygon)) {
        reqTasks.push(..._.cloneDeep(taskList));
      }
    }
  } else if (shape.type === SHAPE_TYPE.CIRCLE) {
    const getCenter = shape.getCenter();
    const center = { lat: getCenter.lat(), lng: getCenter.lng() };
    const radiusInKM = shape.getRadius() / 1000;
    for (const marker of markers) {
      const { location, taskList = [], type } = marker;
      if (type !== STOP_TYPE.USER_CURRENT_LOCATION_STOP && isPointInsideCircle(location, center, radiusInKM)) {
        reqTasks.push(..._.cloneDeep(taskList));
      }
    }
  } else if (shape.type === SHAPE_TYPE.RECTANGLE) {
    const bounds = shape.getBounds();
    const start = bounds.getNorthEast();
    const end = bounds.getSouthWest();
    const ne = [start.lat(), start.lng()];
    const sw = [end.lat(), end.lng()];
    const nw = [start.lat(), end.lng()];
    const se = [end.lat(), start.lng()];
    const rectangle = [ne, nw, sw, se];
    for (const marker of markers) {
      const {
        location: { lat, lng },
        taskList = [],
        type,
      } = marker;
      if (type !== STOP_TYPE.USER_CURRENT_LOCATION_STOP && isPointInPolygon(lat, lng, rectangle)) {
        reqTasks.push(..._.cloneDeep(taskList));
      }
    }
  }
  return reqTasks;
}

export const filterOutStopsOnSelectedUser = (stopsDict, selectedUserIds) => {
  for (const [stopId, stopInfo] of Object.entries(stopsDict)) {
    const {
      location: { lng, lat },
    } = stopInfo;
    if (!lng || !lat) {
      delete stopsDict[stopId];
    } else if (stopInfo.type === STOP_TYPE.TASK_STOP) {
      const { taskUserSequence } = stopsDict[stopId];
      let { sequence } = stopsDict[stopId];
      const filteredTasks = stopsDict[stopId].taskList.filter((t) =>
        TaskUtilities.doesTaskBelongToAnyAssigneeIds(t, selectedUserIds)
      );
      if (selectedUserIds.length === 1) {
        const newFilteredSequence = filteredTasks
          .map((task) => _.get(taskUserSequence, `[${task.id}][${selectedUserIds[0]}]`, []))
          .flatMap((val) => val);
        sequence = newFilteredSequence;
      }
      stopsDict[stopId] = {
        ...stopsDict[stopId],
        taskList: filteredTasks,
        sequence,
      };
      if (!stopsDict[stopId].taskList.length) {
        delete stopsDict[stopId];
      }
    } else {
      const userIdsInStop = stopInfo.userStops.map((user) => user.id);
      if (!_.intersection(userIdsInStop, selectedUserIds).length) {
        if (stopInfo.type === STOP_TYPE.USER_START_END_STOP) {
          const { taskUserSequence } = stopsDict[stopId];
          let { sequence } = stopsDict[stopId];
          const filteredTasks = stopsDict[stopId].taskList.filter((t) =>
            TaskUtilities.doesTaskBelongToAnyAssigneeIds(t, selectedUserIds)
          );
          if (selectedUserIds.length === 1) {
            const newFilteredSequence = filteredTasks
              .map((task) => _.get(taskUserSequence, `[${task.id}][${selectedUserIds[0]}]`, []))
              .flatMap((val) => val);
            sequence = newFilteredSequence;
          }

          stopsDict[stopId] = {
            ...stopsDict[stopId],
            taskList: filteredTasks,
            sequence,
          };
          if (!stopsDict[stopId].taskList.length) {
            delete stopsDict[stopId];
          }
        } else {
          delete stopsDict[stopId];
        }
      } else {
        const { taskUserSequence } = stopsDict[stopId];
        let { sequence } = stopsDict[stopId];
        const filteredTasks = stopsDict[stopId].taskList.filter((t) =>
          TaskUtilities.doesTaskBelongToAnyAssigneeIds(t, selectedUserIds)
        );
        if (selectedUserIds.length === 1) {
          const newFilteredSequence = filteredTasks
            .map((task) => _.get(taskUserSequence, `[${task.id}][${selectedUserIds[0]}]`, []))
            .flatMap((val) => val);
          sequence = newFilteredSequence;
        }

        stopsDict[stopId] = {
          ...stopsDict[stopId],
          taskList: filteredTasks,
          sequence,
        };
      }
    }
  }
};

export const getSequence = (sequence) => {
  if (sequence.length === 1) {
    return `${sequence[0]}`;
  }
  if (sequence.length === 2) {
    return `${sequence[0]}, ${sequence[1]}`;
  }
  return `${sequence[0]}, ${sequence[1]}...`;
};

function getRadialGeofencePolygon({ point, radius }) {
  const polygon = [];
  const bearing = 45;
  const numberOfPoints = 360 / bearing;
  const center = [point.long, point.lat]; // [X or longitude, Y or latitude].

  // eslint-disable-next-line for-direction
  for (let i = numberOfPoints; i >= 0; i -= 1) {
    const variablePoint = computeDestinationPoint(center, radius, i * bearing);
    polygon.push([variablePoint.longitude, variablePoint.latitude]);
  }

  return polygon;
}

function completePolygon(polygon) {
  const len = polygon.length;
  if (polygon[0][0] !== polygon[len - 1][0] || polygon[0][1] !== polygon[len - 1][1]) {
    polygon[len] = JSON.parse(JSON.stringify(polygon[0]));
  }
}

export const getPolygon = (geofence, latitude, longitude) => {
  const { type, radius = '' } = geofence;
  let polygon = null;
  if (type === GEOFENCE_TYPES.RADIAL) {
    if (!latitude || !longitude || isNaN(parseInt(radius)) || !radius) {
      return polygon;
    }
    polygon = getRadialGeofencePolygon({
      point: { lat: latitude, long: longitude },
      radius: typeof radius === 'string' ? parseInt(radius, 10) : radius,
    });
  } else if (type === GEOFENCE_TYPES.POLYGON) {
    polygon = JSON.parse(JSON.stringify(geofence.polygon));
    completePolygon(polygon);
  }
  return (polygon || []).map((item) => {
    const [lng, lat] = item;
    return { lat: parseFloat(lat), lng: parseFloat(lng) };
  });
};
